/**
 * **重大资产详情页**（R 域 R2/R3/R6/R7/R8 · `AC-R2.3`–`R2.5` · `AC-R3.4` · `AC-R6.1`–`R6.5` ·
 * `AC-R7.1`–`R7.4` · `AC-R8.1`–`R8.4`）· 批 92
 *
 * 依据：原型 `16-重大资产_原型.html:265–441`（回报 / 曲线 / 历史 / 关联 / 折旧 + 六个动作）。
 *
 * ## 🔴 页面只做「取数 + 接线」，六个动作的业务约束全在服务端
 *
 * | 动作 | 生成什么 | 端侧责任 |
 * |---|---|---|
 * | 购入 / 追加 / 收益 | **真实交易**（可查可改） | 收集输入 → 纯逻辑层校验 → 提交 |
 * | 调整估值 / 处置 | 台账动作（处置另生一笔资金交易） | 同上 |
 * | 关联贷款 | 整组替换 junction | 只提交 id 列表 |
 *
 * ## 🔴 已处置的资产**不渲染任何动作按钮**（`AC-R8.3`）
 *
 * 不是"画出来但禁用" —— 禁用的按钮仍然在说"这里本来能做这件事"。
 * 处置后这一页只剩：回报收口 + 曲线 + 历史 + 关联（只读）。
 *
 * ## ⚠️ 三个 `invalidate` 的分工
 *
 * | key | 为什么 |
 * |---|---|
 * | `['major-asset', id]` / `['major-assets']` | 本页与列表 |
 * | `['networth']` | 资产/负债变了（`AC-R4.3` / `AC-R7.5`）|
 * | `['accounts']` | 资金动作动了账户余额 |
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  List,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MAJOR_ASSET_INCOME_CATEGORY_IDS, formatMoneyText, type Cents } from '@jiaying/shared';
import { useNavigate, useParams } from 'react-router-dom';
import {
  adjustMajorAssetValuation,
  appendMajorAssetInvestment,
  deleteMajorAsset,
  disposeMajorAsset,
  getMajorAsset,
  listAccounts,
  listLedgerMembers,
  purchaseMajorAsset,
  recordMajorAssetIncome,
  setMajorAssetLoans,
  updateMajorAsset,
} from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { useBaseCurrency } from '../app/useBaseCurrency.js';
import { localToday } from '../app/local-day.js';
import {
  assetDraftFromView,
  buildAppendRequest,
  buildDisposeRequest,
  buildIncomeRequest,
  buildPurchaseRequest,
  buildUpdateAssetRequest,
  buildValuationRequest,
  emptyAppendDraft,
  emptyDisposeDraft,
  emptyIncomeDraft,
  emptyPurchaseDraft,
  emptyValuationDraft,
  validateAppendDraft,
  validateAssetDraft,
  validateDisposeDraft,
  validateIncomeDraft,
  validatePurchaseDraft,
  validateValuationDraft,
  type AppendDraft,
  type AssetFormDraft,
  type DisposeDraft,
  type DraftErrors,
  type IncomeDraft,
  type PurchaseDraft,
  type ValuationDraft,
} from '../features/major-asset.js';
import {
  BTN_ADJUST,
  BTN_APPEND,
  BTN_DELETE,
  BTN_DISPOSE,
  BTN_EDIT,
  BTN_INCOME,
  BTN_PURCHASE,
  DETAIL_DEP_TITLE,
  DETAIL_HISTORY_HINT,
  DETAIL_HISTORY_TITLE,
  DETAIL_LOAN_TITLE,
  DISPOSED_STATE_TEXT,
  DLG_LOANS_TITLE,
  EMPTY_VALUATION_TEXT,
  HIDDEN_SECTION_HINT,
  NO_VALUATION_TEXT,
  TYPE_LABEL,
} from '../features/major-asset-labels.js';
import { Amount } from '../ui/Amount.js';
import { Grid } from '../ui/Grid.js';
import { MajorAssetCurve } from '../ui/MajorAssetCurve.js';
import {
  AppendBody,
  AssetFormDialog,
  DisposeBody,
  IncomeBody,
  LoansBody,
  PurchaseBody,
  ValuationBody,
} from '../ui/MajorAssetDialogs.js';
import { MajorAssetReturnPanel } from '../ui/MajorAssetReturnPanel.js';

/** 七个动作 + 编辑的对话框标识 */
type DialogKind = 'edit' | 'valuation' | 'purchase' | 'append' | 'income' | 'dispose' | 'loans';

export function MajorAssetDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const today = localToday();
  const baseCurrency = useBaseCurrency();

  const asset = useQuery({
    queryKey: ['major-asset', id],
    queryFn: () => getMajorAsset(id),
    enabled: id !== '',
  });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const members = useQuery({ queryKey: ['ledger-members'], queryFn: listLedgerMembers });

  const [open, setOpen] = useState<DialogKind | null>(null);
  const [errors, setErrors] = useState<DraftErrors>({});
  const [confirmDispose, setConfirmDispose] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── 草稿（`null` = 尚未打开过；打开时在**渲染期**用 `??` 派生，不在 effect 里初始化） ──
  const [editDraft, setEditDraft] = useState<AssetFormDraft | null>(null);
  const [valuationDraft, setValuationDraft] = useState<ValuationDraft | null>(null);
  const [purchaseDraft, setPurchaseDraft] = useState<PurchaseDraft | null>(null);
  const [appendDraft, setAppendDraft] = useState<AppendDraft | null>(null);
  const [incomeDraft, setIncomeDraft] = useState<IncomeDraft | null>(null);
  const [disposeDraft, setDisposeDraft] = useState<DisposeDraft | null>(null);
  const [loanSelection, setLoanSelection] = useState<string[] | null>(null);

  const view = asset.data;

  /** 所有写动作成功后统一刷这四组 key */
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['major-asset', id] });
    void qc.invalidateQueries({ queryKey: ['major-assets'] });
    void qc.invalidateQueries({ queryKey: ['networth'] });
    void qc.invalidateQueries({ queryKey: ['accounts'] });
  };

  const closeAll = () => {
    setOpen(null);
    setErrors({});
    setConfirmDispose(false);
  };

  const saveEdit = useMutation({
    mutationFn: (d: AssetFormDraft) => updateMajorAsset(id, buildUpdateAssetRequest(d)),
    onSuccess: () => {
      refresh();
      closeAll();
    },
  });
  const saveValuation = useMutation({
    mutationFn: (d: ValuationDraft) => adjustMajorAssetValuation(id, buildValuationRequest(d)),
    onSuccess: () => {
      refresh();
      closeAll();
    },
  });
  const doPurchase = useMutation({
    mutationFn: (d: PurchaseDraft) => purchaseMajorAsset(id, buildPurchaseRequest(d)),
    onSuccess: () => {
      refresh();
      setPurchaseDraft(null);
      closeAll();
    },
  });
  const doAppend = useMutation({
    mutationFn: (d: AppendDraft) => appendMajorAssetInvestment(id, buildAppendRequest(d)),
    onSuccess: () => {
      refresh();
      setAppendDraft(null);
      closeAll();
    },
  });
  const doIncome = useMutation({
    mutationFn: (d: IncomeDraft) => recordMajorAssetIncome(id, buildIncomeRequest(d)),
    onSuccess: () => {
      refresh();
      setIncomeDraft(null);
      closeAll();
    },
  });
  const doDispose = useMutation({
    mutationFn: (d: DisposeDraft) => disposeMajorAsset(id, buildDisposeRequest(d)),
    onSuccess: () => {
      refresh();
      setDisposeDraft(null);
      closeAll();
    },
  });
  const doLoans = useMutation({
    mutationFn: (ids: string[]) => setMajorAssetLoans(id, { contraIds: ids }),
    onSuccess: () => {
      refresh();
      setLoanSelection(null);
      closeAll();
    },
  });
  const doDelete = useMutation({
    mutationFn: () => deleteMajorAsset(id),
    onSuccess: () => {
      refresh();
      navigate('/major-assets');
    },
    onError: (e) => setDeleteError(e instanceof ApiError ? e.message : String(e)),
  });

  /** 贷款类账户（`contraKind === 'loan'`）—— 关联目标只能是它（`AC-R3.1`） */
  const loanAccounts = useMemo(
    () => (accounts.data?.items ?? []).filter((a) => a.contraKind === 'loan'),
    [accounts.data],
  );
  /** 可用于付款/收款的日常账户（排除 contra：借贷账户不是"钱在哪"） */
  const dailyAccounts = useMemo(
    () => (accounts.data?.items ?? []).filter((a) => a.type !== 'contra'),
    [accounts.data],
  );

  /** 估值历史：**服务端升序** ⇒ 展示倒序（最新在上），只是 reverse，不重排 | 不过滤 */
  const history = useMemo(() => [...(view?.valuations ?? [])].reverse(), [view?.valuations]);

  if (asset.isPending) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }} data-testid="ma-detail-loading">
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (asset.isError || view === undefined) {
    return (
      <Stack spacing={2} data-testid="ma-detail-error">
        <Alert severity="error">资产读取失败：{String(asset.error)}</Alert>
        <Button onClick={() => navigate('/major-assets')}>返回列表</Button>
      </Stack>
    );
  }

  const disposed = view.disposedDate !== null;
  const currentCurrency = view.currency || baseCurrency || '';
  const mutationError =
    saveEdit.error ??
    saveValuation.error ??
    doPurchase.error ??
    doAppend.error ??
    doIncome.error ??
    doDispose.error ??
    doLoans.error ??
    null;

  return (
    <Stack spacing={2} data-testid="major-asset-detail">
      {/* ── 头部 ── */}
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" rowGap={0.5}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              {view.name}
            </Typography>
            <Chip size="small" label={TYPE_LABEL[view.type]} />
            {view.hidden && <Chip size="small" label="已隐藏" data-testid="ma-detail-hidden" />}
            {disposed && (
              <Chip
                size="small"
                color="default"
                variant="outlined"
                label={`${view.disposedDate ?? ''} ${DISPOSED_STATE_TEXT}`}
                data-testid="ma-detail-disposed"
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            归属 {view.ownerName || '—'}
            {view.location !== null && ` · ${view.location}`}
            {view.purchaseDate !== null && ` · 购入 ${view.purchaseDate}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            onClick={() => {
              setEditDraft(assetDraftFromView(view));
              setErrors({});
              setOpen('edit');
            }}
            disabled={disposed}
            data-testid="ma-edit"
          >
            {BTN_EDIT}
          </Button>
          {confirmDelete ? (
            <>
              <Button
                size="small"
                color="error"
                variant="contained"
                disabled={doDelete.isPending}
                onClick={() => doDelete.mutate()}
                data-testid="ma-delete-confirm"
              >
                再点一次删除
              </Button>
              <Button size="small" onClick={() => setConfirmDelete(false)}>
                取消
              </Button>
            </>
          ) : (
            <Button
              size="small"
              color="error"
              onClick={() => setConfirmDelete(true)}
              disabled={doDelete.isPending}
              data-testid="ma-delete"
            >
              {BTN_DELETE}
            </Button>
          )}
        </Stack>
      </Stack>

      {deleteError !== null && (
        <Alert severity="error" onClose={() => setDeleteError(null)}>
          删除失败：{deleteError}
        </Alert>
      )}

      {mutationError !== null && (
        <Alert severity="error" data-testid="ma-action-error">
          {mutationError instanceof ApiError
            ? `${mutationError.message}（HTTP ${String(mutationError.status)}）`
            : String(mutationError)}
        </Alert>
      )}

      {/* 已处置：只读提示（`AC-R8.3`：动作按钮不再出现） */}
      {disposed && (
        <Alert severity="info" data-testid="ma-disposed-banner">
          这项资产已于 {view.disposedDate} 处置 —— 回报已收口，购入 / 追加 / 收益 / 调估值都不再出现；
          删除那笔处置交易会让销账自动回滚。
        </Alert>
      )}

      {/* ── 动作条（已处置则不渲染） ── */}
      {!disposed && (
        <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} data-testid="ma-actions">
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setPurchaseDraft(emptyPurchaseDraft(today));
              setErrors({});
              setOpen('purchase');
            }}
            data-testid="ma-action-purchase"
          >
            {BTN_PURCHASE}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setAppendDraft(emptyAppendDraft(today));
              setErrors({});
              setOpen('append');
            }}
            data-testid="ma-action-append"
          >
            {BTN_APPEND}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setIncomeDraft(emptyIncomeDraft(today, MAJOR_ASSET_INCOME_CATEGORY_IDS[0]));
              setErrors({});
              setOpen('income');
            }}
            data-testid="ma-action-income"
          >
            {BTN_INCOME}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setValuationDraft(emptyValuationDraft(today, view.estimatedValue));
              setErrors({});
              setOpen('valuation');
            }}
            data-testid="ma-action-valuation"
          >
            {BTN_ADJUST}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setLoanSelection(view.loans.map((l) => l.accountId));
              setErrors({});
              setOpen('loans');
            }}
            data-testid="ma-action-loans"
          >
            {DLG_LOANS_TITLE}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={() => {
              setDisposeDraft(emptyDisposeDraft(today, view.loans.length > 0));
              setConfirmDispose(false);
              setErrors({});
              setOpen('dispose');
            }}
            data-testid="ma-action-dispose"
          >
            {BTN_DISPOSE}
          </Button>
        </Stack>
      )}

      {/* ── 回报面板 ── */}
      <Card>
        <CardContent>
          <MajorAssetReturnPanel view={view} />
        </CardContent>
      </Card>

      {/* ── 曲线 + 估值历史 ── */}
      <Grid cols={2} sx={{ alignItems: 'start' }}>
        <Card data-testid="ma-curve-card">
          <CardContent>
            <MajorAssetCurve points={view.valuations} currency={currentCurrency} />
          </CardContent>
        </Card>

        <Card data-testid="ma-history-card">
          <CardContent sx={{ pb: 0.5 }}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {DETAIL_HISTORY_TITLE}（{history.length}）
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                {DETAIL_HISTORY_HINT}
              </Typography>
              {!disposed && (
                <Button
                  size="small"
                  onClick={() => {
                    setValuationDraft(emptyValuationDraft(today, view.estimatedValue));
                    setErrors({});
                    setOpen('valuation');
                  }}
                >
                  {BTN_ADJUST}
                </Button>
              )}
            </Stack>
          </CardContent>
          <Divider />
          {history.length === 0 ? (
            <CardContent>
              <Typography variant="caption" color="text.secondary" data-testid="ma-history-empty">
                {EMPTY_VALUATION_TEXT}
              </Typography>
            </CardContent>
          ) : (
            <List dense disablePadding sx={{ px: 1, py: 0.5 }}>
              {history.map((h, i) => (
                <Box
                  key={h.id}
                  data-testid={`ma-history-row-${String(i)}`}
                  sx={{
                    py: 0.75,
                    borderBottom: i === history.length - 1 ? 'none' : 1,
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: i === 0 ? 600 : 400 }}>
                      {formatMoneyText(h.value as Cents, h.currency)}
                    </Typography>
                    {i === 0 && <Chip size="small" color="primary" label="当前" />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {h.valuationDate}
                    {h.note !== null && h.note !== '' ? ` · ${h.note}` : ''}
                  </Typography>
                </Box>
              ))}
            </List>
          )}
        </Card>
      </Grid>

      {/* ── 关联贷款 + 折旧 ── */}
      <Grid cols={2} sx={{ alignItems: 'start' }}>
        <Card data-testid="ma-loans-card">
          <CardContent sx={{ pb: 0.5 }}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {DETAIL_LOAN_TITLE}（{view.loans.length}）
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                剩余合计 {formatMoneyText(view.loanRemainingCents as Cents, currentCurrency)}
              </Typography>
            </Stack>
          </CardContent>
          <Divider />
          {view.loans.length === 0 ? (
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                没有关联贷款 —— 「贷款买入」会自动挂上，也可手动管理
              </Typography>
            </CardContent>
          ) : (
            <List dense disablePadding sx={{ px: 1, py: 0.5 }}>
              {view.loans.map((l) => (
                <Stack
                  key={l.accountId}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 0.75 }}
                  data-testid={`ma-loan-row-${l.accountId}`}
                >
                  <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                    {l.name}
                  </Typography>
                  <Amount
                    cents={l.remainingCents as Cents}
                    currency={l.currency}
                    colored={false}
                    variant="body2"
                  />
                </Stack>
              ))}
            </List>
          )}
          <CardContent sx={{ pt: 0 }}>
            <Typography variant="caption" color="text.secondary">
              还款走「债权债务 · 贷款」既有入口，本域不新建还款路径
            </Typography>
          </CardContent>
        </Card>

        <Card data-testid="ma-depreciation-card">
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {DETAIL_DEP_TITLE}
            </Typography>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  购置成本
                </Typography>
                <Typography variant="body2">
                  {view.purchaseCost === null
                    ? NO_VALUATION_TEXT
                    : formatMoneyText(view.purchaseCost as Cents, currentCurrency)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  累计折旧（{view.depMethod === 'straight' ? `直线法 ${String(view.depYears ?? '')} 年` : '不折旧'}）
                </Typography>
                <Typography variant="body2">
                  {view.depreciation === null
                    ? '—'
                    : formatMoneyText(view.depreciation as Cents, currentCurrency)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 600 }}>
                  账面净值
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {view.bookValueCents === null
                    ? '—'
                    : formatMoneyText(view.bookValueCents as Cents, currentCurrency)}
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              账面净值只做展示：不进净资产、不进回报（净资产按当前估值全额计入）
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Stack direction="row" spacing={1} alignItems="center">
        <Button size="small" onClick={() => navigate('/major-assets')} data-testid="ma-back">
          ← 返回列表
        </Button>
        <Typography variant="caption" color="text.secondary">
          当前估值{' '}
          {view.estimatedValue === null
            ? NO_VALUATION_TEXT
            : formatMoneyText(view.estimatedValue as Cents, currentCurrency)}
          {view.hidden ? ` · ${HIDDEN_SECTION_HINT}` : ''}
        </Typography>
      </Stack>

      {/* ── 对话框们 ── */}
      {editDraft !== null && (
        <AssetFormDialog
          open={open === 'edit'}
          onClose={closeAll}
          draft={editDraft}
          errors={errors}
          busy={saveEdit.isPending}
          mode="edit"
          members={members.data ?? []}
          onChange={(patch) => setEditDraft((d) => (d === null ? d : { ...d, ...patch }))}
          onSubmit={() => {
            const e = validateAssetDraft(editDraft);
            setErrors(e);
            if (Object.keys(e).length === 0) saveEdit.mutate(editDraft);
          }}
        />
      )}

      {valuationDraft !== null && (
        <DialogShell open={open === 'valuation'} onClose={closeAll}>
          <ValuationBody
            draft={valuationDraft}
            errors={errors}
            busy={saveValuation.isPending}
            assetName={view.name}
            onChange={(patch) =>
              setValuationDraft((d) => (d === null ? d : { ...d, ...patch }))
            }
            onCancel={closeAll}
            onSubmit={() => {
              const e = validateValuationDraft(valuationDraft);
              setErrors(e);
              if (Object.keys(e).length === 0) saveValuation.mutate(valuationDraft);
            }}
          />
        </DialogShell>
      )}

      {purchaseDraft !== null && (
        <DialogShell open={open === 'purchase'} onClose={closeAll} maxWidth="sm">
          <PurchaseBody
            draft={purchaseDraft}
            errors={errors}
            busy={doPurchase.isPending}
            assetName={view.name}
            accounts={dailyAccounts}
            loanAccounts={loanAccounts}
            onChange={(patch) => setPurchaseDraft((d) => (d === null ? d : { ...d, ...patch }))}
            onCancel={closeAll}
            onSubmit={() => {
              const e = validatePurchaseDraft(purchaseDraft);
              setErrors(e);
              if (Object.keys(e).length === 0) doPurchase.mutate(purchaseDraft);
            }}
          />
        </DialogShell>
      )}

      {appendDraft !== null && (
        <DialogShell open={open === 'append'} onClose={closeAll}>
          <AppendBody
            draft={appendDraft}
            errors={errors}
            busy={doAppend.isPending}
            assetName={view.name}
            accounts={dailyAccounts}
            onChange={(patch) => setAppendDraft((d) => (d === null ? d : { ...d, ...patch }))}
            onCancel={closeAll}
            onSubmit={() => {
              const e = validateAppendDraft(appendDraft);
              setErrors(e);
              if (Object.keys(e).length === 0) doAppend.mutate(appendDraft);
            }}
          />
        </DialogShell>
      )}

      {incomeDraft !== null && (
        <DialogShell open={open === 'income'} onClose={closeAll}>
          <IncomeBody
            draft={incomeDraft}
            errors={errors}
            busy={doIncome.isPending}
            assetName={view.name}
            accounts={dailyAccounts}
            onChange={(patch) => setIncomeDraft((d) => (d === null ? d : { ...d, ...patch }))}
            onCancel={closeAll}
            onSubmit={() => {
              const e = validateIncomeDraft(incomeDraft, MAJOR_ASSET_INCOME_CATEGORY_IDS);
              setErrors(e);
              if (Object.keys(e).length === 0) doIncome.mutate(incomeDraft);
            }}
          />
        </DialogShell>
      )}

      {disposeDraft !== null && (
        <DialogShell open={open === 'dispose'} onClose={closeAll}>
          <DisposeBody
            draft={disposeDraft}
            errors={errors}
            busy={doDispose.isPending}
            assetName={view.name}
            accounts={dailyAccounts}
            hasLoans={view.loans.length > 0}
            confirming={confirmDispose}
            onChange={(patch) => setDisposeDraft((d) => (d === null ? d : { ...d, ...patch }))}
            onCancel={closeAll}
            onSubmit={() => {
              const e = validateDisposeDraft(disposeDraft);
              setErrors(e);
              if (Object.keys(e).length > 0) return;
              // 🔴 处置不可逆 ⇒ 第一次点击只进入"再点一次"态（同 `AC-Q2.5` 核销范式）
              if (!confirmDispose) {
                setConfirmDispose(true);
                return;
              }
              doDispose.mutate(disposeDraft);
            }}
          />
        </DialogShell>
      )}

      {loanSelection !== null && (
        <DialogShell open={open === 'loans'} onClose={closeAll} maxWidth="xs">
          <LoansBody
            busy={doLoans.isPending}
            assetName={view.name}
            loanAccounts={loanAccounts}
            selected={loanSelection}
            onToggle={(accountId) =>
              setLoanSelection((s) =>
                s === null
                  ? s
                  : s.includes(accountId)
                    ? s.filter((x) => x !== accountId)
                    : [...s, accountId],
              )
            }
            onCancel={closeAll}
            onSubmit={() => doLoans.mutate(loanSelection)}
          />
        </DialogShell>
      )}
    </Stack>
  );
}

/** 统一的外层 `Dialog` 壳（六个 Body 都只关心内容） */
function DialogShell({
  open,
  onClose,
  maxWidth = 'xs',
  children,
}: {
  open: boolean;
  onClose: () => void;
  maxWidth?: 'xs' | 'sm';
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={maxWidth}>
      {children}
    </Dialog>
  );
}
